import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { copyToClipboard } from '../utils/clipboard';
import { CONTACT_EMAIL, QUOTE_URL, mailto } from '../lib/site';

const CopyEmailButton: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleClick = async () => {
    const ok = await copyToClipboard(CONTACT_EMAIL);
    setStatus(ok ? 'copied' : 'failed');
    setTimeout(() => setStatus('idle'), 2000);
  };

  const text = status === 'copied' ? 'Copied!' : status === 'failed' ? 'Copy failed' : 'Copy email';

  return (
    <Button type="button" variant="outline" onClick={handleClick} title="Copy email address">
      {text}
      <span className="sr-only" role="status" aria-live="polite">
        {status === 'copied' ? 'Email address copied to clipboard' : status === 'failed' ? 'Copy failed; try again' : ''}
      </span>
    </Button>
  );
};

const rowLink =
  'font-semibold text-primary-300 hover:text-primary-200 underline underline-offset-4 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';

const CHANNELS: { title: string; desc: string; action: { label: string; to?: string; href?: string; external?: boolean } }[] = [
  {
    title: 'Development quote',
    desc: 'Get a quote for a website or application.',
    action: { label: 'Get a quote', href: QUOTE_URL, external: true },
  },
  {
    title: 'General questions',
    desc: 'Anything about FairCorp, its products, or a project you have in mind.',
    action: { label: 'Send an email', href: mailto('Question for FairCorp') },
  },
  {
    title: 'Partnerships',
    desc: 'Apply to partner with FairCorp.',
    action: { label: 'Partnership form', to: '/apply/partnership' },
  },
  {
    title: 'Careers',
    desc: 'No positions are currently available. Applications are not open.',
    action: { label: 'Careers', to: '/apply/position' },
  },
  {
    title: 'Support',
    desc: 'Tickets are currently unavailable. Email us and put the service in the subject line.',
    action: { label: 'Email support', href: mailto('Service: ') },
  },
];

const Contact: React.FC = () => (
  <div style={{ background: 'radial-gradient(ellipse 80% 40% at 20% 0%, rgba(56,189,248,0.10) 0%, transparent 62%), radial-gradient(ellipse 60% 35% at 85% 5%, rgba(217,70,239,0.07) 0%, transparent 62%)' }}>
    <section className="page-shell page-section-major">
      <div className="page-inner">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-2">
            <span className="cw">Contact </span>
            <span className="cb">FairCorp</span>
          </h1>
          <p className="text-secondary-400">Email is the main way to reach us. Pick a channel below for anything more specific.</p>
        </header>

        <div className="rounded-2xl border border-secondary-700/60 bg-secondary-900 p-5 sm:p-6 mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1">Email</p>
          <a
            href={mailto()}
            className="block text-xl sm:text-2xl font-bold text-primary-300 hover:text-primary-200 break-all rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            {CONTACT_EMAIL}
          </a>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button href={mailto()}>Open in email</Button>
            <CopyEmailButton />
          </div>
        </div>

        <h2 className="text-lg font-bold text-white mb-1">Which channel?</h2>
        <p className="text-sm text-secondary-500 mb-3">The partnership form submits to our internal helpdesk. Careers and support tickets are not open.</p>
        <ul className="divide-y divide-secondary-800/70 border-y border-secondary-800/70">
          {CHANNELS.map((c) => (
            <li key={c.title} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-6">
              <div className="min-w-0">
                <h3 className="font-semibold text-white leading-tight">{c.title}</h3>
                <p className="text-sm text-secondary-400">{c.desc}</p>
              </div>
              {c.action.to ? (
                <Link to={c.action.to} className={`${rowLink} shrink-0 inline-flex items-center min-h-[44px]`}>{c.action.label} →</Link>
              ) : (
                <a href={c.action.href} className={`${rowLink} shrink-0 inline-flex items-center min-h-[44px]`}>{c.action.label} {c.action.external ? '↗' : '→'}</a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  </div>
);

export default Contact;
